"use client";
/**
 * EditableRequestCard.jsx — FULL REPLACEMENT
 *
 * Fixes:
 *  1. Module items now always visible in edit mode.
 *     - After save, card collapses (setExpanded(false)) so re-opening
 *       re-fires the load useEffect cleanly with fresh data.
 *     - useEffect depends on request.lineItems so it re-runs after save.
 *     - enterEditMode() always rebuilds instances from current module +
 *       request.lineItems, never relies on stale state.
 *
 *  2. HR row rate sourced from saved lineItem unitRate, not /auth/me.
 *     /auth/me returns the foreman's rate. The saved HR lineItem already
 *     has the correct worker rate — use hrUnitRate derived from that.
 *
 *  3. HR row always included in PATCH payload when hours > 0.
 *
 *  4. Save guard includes HR rows so hours-only saves go through.
 *
 *  5. Zero-quantity module rows hidden in read-only view.
 *
 *  6. HR row shown in read-only view via readOnlyManualItems.
 */

import { useState, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import { format, parseISO } from "date-fns";
import SharedPieceworkItemEntry, {
  calcLaborQtyInst,
  makeInst,
  parseInches,
  parseFt,
  validateEntries,
  isSF,
  isLF,
  requiresDims,
  calcManualQty,
} from "./SharedPieceworkItemEntry";

const API = "https://erp-production-72da01c8e651.herokuapp.com/api";

const fmt = (n) =>
  (n ?? 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
const fmtQty = (n) =>
  (n ?? 0).toLocaleString("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
// Rates need finer precision than money totals — piecework rates are
// often fractional (e.g. $0.4375/SF) and rounding to 2dp silently
// changes what the worker is actually being paid per unit.
const fmtRate = (n) =>
  (n ?? 0).toLocaleString("en-US", {
    minimumFractionDigits: 4,
    maximumFractionDigits: 4,
  });
const cls = (...c) => c.filter(Boolean).join(" ");

const resolveEntryFt = (val) => {
  if (val == null || val === "") return 0;
  const n = parseFt(String(val));
  return n !== null ? n : parseFloat(val) || 0;
};

// Returns ALL saved labor rows for a paired slot.
// Multiple rows share the same moduleItemId when a worker submitted several
// sizes under one item (each size is its own line item with the same mid).
const findLaborRowsForPaired = (lineItems, moduleId, pIdx, paired) => {
  const targetMid = `${moduleId}_${pIdx}`;
  const laborCode = (paired.labor?.itemCode || "").trim();

  // 1. Exact moduleItemId matches (all of them)
  const byMid = (lineItems || []).filter(
    (li) =>
      li.moduleItemId === targetMid &&
      li.type !== "material" &&
      !li.poItemCode &&
      !!li.itemCode &&
      li.uom !== "HR",
  );
  if (byMid.length) return byMid;

  // 2. Legacy fallback — itemCode, but only rows that don't carry a
  //    moduleItemId (so we never steal a row that belongs to another slot)
  return (lineItems || []).filter(
    (li) =>
      (li.itemCode || "").trim() === laborCode &&
      li.type !== "material" &&
      !li.poItemCode &&
      !!li.itemCode &&
      li.uom !== "HR" &&
      !li.moduleItemId,
  );
};

// Singular convenience wrapper — first matching row.
const findLaborRowForPaired = (lineItems, moduleId, pIdx, paired) =>
  findLaborRowsForPaired(lineItems, moduleId, pIdx, paired)[0] || null;

const STATUS_BADGE = {
  submitted: "bg-yellow-700/60 text-yellow-100",
  confirmed: "bg-green-700/60 text-green-200",
  rejected: "bg-red-800/60 text-red-200",
  draft: "bg-gray-700/60 text-gray-300",
};
const STATUS_BORDER = {
  submitted: "border-yellow-700/30",
  confirmed: "border-green-700/30",
  rejected: "border-red-700/30",
  draft: "border-gray-700",
};

// ---------------------------------------------------------------------------
// Read-only summary table (confirmed / rejected views)
// ---------------------------------------------------------------------------
function LaborSummaryTable({ items, hstRate, workerBurdenMultiplier = 1 }) {
  const showHst = (hstRate || 0) > 0;

  const laborRows = (items || []).filter(
    (li) => li.type !== "material" && !li.poItemCode && !!li.itemCode,
  );
  let totalSub = 0;
  const rows = laborRows.map((item) => {
    const needsDims = requiresDims(item.uom);
    const coverage = item.computedCoverage || 0;
    const qty = needsDims ? coverage : item.quantity || 0;
    const wRate = item.unitRate || 0;

    const sub = parseFloat((qty * wRate).toFixed(2));
    totalSub += sub;
    return { ...item, effectiveQty: qty, wRate, sub, needsDims, coverage };
  });
  const totalHst = showHst
    ? parseFloat(((totalSub * hstRate) / 100).toFixed(2))
    : 0;

  const headers = [
    "Description",
    "Area",
    "Qty / Coverage",
    "Rate",
    "Subtotal",
    ...(showHst ? ["HST", "Total"] : []),
  ];

  // Match a saved labor row to a specific paired index.
  // Prefers moduleItemId (handles duplicate itemCodes across paired slots),
  // falls back to itemCode for legacy rows submitted before moduleItemId existed.
  // Match a saved labor row to a specific paired index.
  // Prefers moduleItemId (handles duplicate itemCodes across paired slots),
  // falls back to itemCode for legacy rows submitted before moduleItemId existed.

  return (
    <div className="overflow-x-auto rounded-xl border border-gray-700">
      <table className="w-full text-xs">
        <thead className="bg-gray-900">
          <tr>
            {headers.map((h) => (
              <th
                key={h}
                className="px-3 py-2.5 text-left text-gray-400 font-medium whitespace-nowrap"
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-700/50">
          {rows.map((row, idx) => {
            const hst = showHst
              ? parseFloat(((row.sub * hstRate) / 100).toFixed(2))
              : 0;
            return (
              <tr
                key={idx}
                className={idx % 2 === 0 ? "bg-gray-800/40" : "bg-gray-700/20"}
              >
                <td className="px-3 py-2 text-gray-200 max-w-[160px] truncate">
                  {row.description}
                </td>
                <td className="px-3 py-2 text-gray-500 text-[10px]">
                  {row.areaType || row.sectionName || "—"}
                </td>
                <td className="px-3 py-2 text-blue-300 font-mono font-semibold">
                  {row.needsDims && row.coverage > 0
                    ? `${fmtQty(row.coverage)} ${row.uom?.toUpperCase().includes("LF") ? "LF" : "SF"}`
                    : fmtQty(row.effectiveQty)}
                  <span className="text-gray-500 ml-1 font-normal">
                    {row.uom}
                  </span>
                </td>
                <td className="px-3 py-2 text-gray-300 font-mono">
                  ${fmtRate(row.wRate)}
                </td>
                <td className="px-3 py-2 text-gray-200 font-mono">
                  ${fmt(row.sub)}
                </td>
                {showHst && (
                  <td className="px-3 py-2 text-gray-400 font-mono">
                    ${fmt(hst)}
                  </td>
                )}
                {showHst && (
                  <td className="px-3 py-2 text-orange-300 font-mono font-semibold">
                    ${fmt(row.sub + hst)}
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
        <tfoot className="bg-gray-900 border-t-2 border-orange-400/30">
          <tr>
            <td
              colSpan={showHst ? 4 : 4}
              className="px-3 py-2.5 text-xs text-gray-400 font-semibold"
            >
              Labor Totals
            </td>
            <td className="px-3 py-2.5 font-bold text-gray-200 font-mono">
              ${fmt(totalSub)}
            </td>
            {showHst && (
              <td className="px-3 py-2.5 font-bold text-gray-300 font-mono">
                ${fmt(totalHst)}
              </td>
            )}
            {showHst && (
              <td className="px-3 py-2.5 font-bold text-orange-300 font-mono text-sm">
                ${fmt(totalSub + totalHst)}
              </td>
            )}
          </tr>
        </tfoot>
      </table>
    </div>
  );
}
// ---------------------------------------------------------------------------
// Timeline component
// ---------------------------------------------------------------------------
function RequestTimeline({ timeline }) {
  if (!timeline || timeline.length === 0) return null;

  const EVENT_CONFIG = {
    created: {
      icon: "✦",
      color: "text-gray-400",
      bg: "bg-gray-700",
      label: "Created",
    },
    edited: {
      icon: "✎",
      color: "text-blue-400",
      bg: "bg-blue-900/50",
      label: "Edited",
    },
    resubmitted: {
      icon: "↩",
      color: "text-purple-400",
      bg: "bg-purple-900/50",
      label: "Resubmitted",
    },
    confirmed: {
      icon: "✓",
      color: "text-green-400",
      bg: "bg-green-900/50",
      label: "Confirmed",
    },
    rejected: {
      icon: "✕",
      color: "text-red-400",
      bg: "bg-red-900/50",
      label: "Rejected",
    },
  };

  return (
    <div className="space-y-1">
      <h4 className="text-xs font-semibold text-white px-1">Timeline</h4>
      <div className="bg-gray-900/60 rounded-xl border border-gray-700 divide-y divide-gray-700/50">
        {[...timeline].reverse().map((entry, idx) => {
          const cfg = EVENT_CONFIG[entry.event] || EVENT_CONFIG.edited;
          const snap = entry.snapshot || {};
          const changes = snap.changes || [];

          return (
            <div key={idx} className="flex items-start gap-3 px-3 py-2.5">
              {/* Icon dot */}
              <div
                className={cls(
                  "w-5 h-5 rounded-full flex items-center justify-center shrink-0 mt-0.5 text-[10px] font-bold",
                  cfg.bg,
                  cfg.color,
                )}
              >
                {cfg.icon}
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0 space-y-1">
                {/* Header row: event label + actor + role + timestamp */}
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span
                      className={cls("text-[11px] font-semibold", cfg.color)}
                    >
                      {cfg.label}
                    </span>
                    {entry.actorName && (
                      <span className="text-[10px] text-gray-400">
                        by {entry.actorName}
                      </span>
                    )}
                    {entry.actorRole && (
                      <span className="text-[9px] bg-gray-700 text-gray-500 px-1.5 py-0.5 rounded-full capitalize">
                        {entry.actorRole.replace(/_/g, " ")}
                      </span>
                    )}
                  </div>
                  {/* Timestamp — moved inline with header */}
                  {entry.createdAt && (
                    <span className="text-[9px] text-gray-600 font-mono shrink-0">
                      {format(parseISO(entry.createdAt), "MMM d, h:mm a")}
                    </span>
                  )}
                </div>

                {/* Italic note (rejection reason, on-behalf-of, etc.) */}
                {entry.note && (
                  <p className="text-[10px] text-gray-400 italic">
                    "{entry.note}"
                  </p>
                )}

                {/* Per-item change list */}
                {changes.length > 0 && (
                  <ul className="space-y-0.5 mt-1">
                    {changes.map((line, cIdx) => {
                      // Colour-code lines by prefix
                      const isAdded = line.startsWith("Added:");
                      const isRemoved = line.startsWith("Removed:");
                      const isDate = line.startsWith("Work date:");
                      const isNotes = line.startsWith("Notes");
                      const textColor = isAdded
                        ? "text-green-400"
                        : isRemoved
                          ? "text-red-400"
                          : isDate || isNotes
                            ? "text-gray-500"
                            : "text-blue-300";
                      const bullet = isAdded ? "+" : isRemoved ? "−" : "·";
                      return (
                        <li
                          key={cIdx}
                          className={cls(
                            "flex items-baseline gap-1.5 text-[10px]",
                            textColor,
                          )}
                        >
                          <span className="shrink-0 font-mono">{bullet}</span>
                          <span>{line}</span>
                        </li>
                      );
                    })}
                  </ul>
                )}

                {/* Summary badges — only shown when there's no changes list */}
                {changes.length === 0 &&
                  (snap.lineItemCount > 0 ||
                    snap.totalCost > 0 ||
                    snap.workCompletedDate) && (
                    <div className="flex items-center gap-2 flex-wrap">
                      {snap.lineItemCount > 0 && (
                        <span className="text-[9px] bg-gray-800 border border-gray-700 text-gray-400 px-1.5 py-0.5 rounded">
                          {snap.lineItemCount} item
                          {snap.lineItemCount !== 1 ? "s" : ""}
                        </span>
                      )}
                      {snap.totalCost > 0 && (
                        <span className="text-[9px] bg-gray-800 border border-gray-700 text-orange-400 font-mono px-1.5 py-0.5 rounded">
                          $
                          {snap.totalCost.toLocaleString("en-US", {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                        </span>
                      )}
                      {snap.workCompletedDate && (
                        <span className="text-[9px] bg-gray-800 border border-gray-700 text-gray-500 px-1.5 py-0.5 rounded">
                          work: {snap.workCompletedDate}
                        </span>
                      )}
                    </div>
                  )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
// ---------------------------------------------------------------------------
// Build initial instances[] from a module + saved lineItems.
// Every pairedItem gets exactly one instance.
// Items with saved data are pre-filled; items without get blank inputs
// (with module defaults where available) — matching the create flow.
// ---------------------------------------------------------------------------
// ---------------------------------------------------------------------------
// Build initial instances[] from a module + saved lineItems.
// Every pairedItem gets exactly one instance.
// Items with saved data are pre-filled; items without get blank inputs
// (with module defaults where available) — matching the create flow.
//
// Rows are matched to paired slots by moduleItemId first, so duplicate
// itemCodes across different paired slots stay distinct.
// ---------------------------------------------------------------------------
// ---------------------------------------------------------------------------
// Build initial instances[] from a module + saved lineItems.
// Every saved labor row becomes its own instance — paired slots with
// multiple sizes (sharing the same moduleItemId) get multiple instances.
// Items with no saved data get exactly one blank instance with module defaults.
//
// Rows are matched to paired slots by moduleItemId first, so duplicate
// itemCodes across different paired slots stay distinct.
// ---------------------------------------------------------------------------
function buildInitialInstances(mod, lineItems) {
  if (!mod) return [];
  const instances = [];

  (mod.pairedItems || []).forEach((paired, pIdx) => {
    const laborUom = paired.labor?.uom || "";

    const laborRows = findLaborRowsForPaired(lineItems, mod._id, pIdx, paired);

    if (!laborRows.length) {
      // No saved data — use module defaults
      const defH =
        paired.labor?.defaultHeightFt || paired.material?.defaultHeightFt || 0;
      const defL =
        paired.labor?.defaultLengthFt || paired.material?.defaultLengthFt || 0;
      const defIn =
        paired.labor?.defaultLengthInches ||
        paired.material?.defaultLengthInches ||
        0;
      const lfDefault =
        defIn > 0
          ? String(defIn)
          : defL > 0
            ? String(parseFloat((defL * 12).toFixed(4)))
            : "";
      instances.push(
        makeInst(pIdx, {
          qty: "",
          lengthIn: lfDefault,
          height: defH > 0 ? String(defH) : "",
          length: defL > 0 ? String(defL) : "",
          combinedDim: defH > 0 && defL > 0 ? `${defH}x${defL}` : "",
        }),
      );
      return;
    }

    const hasMaterial = !!paired.material;

    // One instance per saved labor row.
    laborRows.forEach((laborRow) => {
      const h = laborRow.height || 0;
      const l = laborRow.length || 0;
      const computedCov = laborRow.computedCoverage || 0;
      const storedQty = laborRow.quantity || 0;
      const totalCoverage = computedCov > 0 ? computedCov : storedQty;

      let pieceQty = "";
      let lengthIn = "";
      let heightStr = "";
      let lengthStr = "";
      let combinedDim = "";

      if (hasMaterial) {
        // Material+labor: recover piece count from material tracking row
        const matRow = (lineItems || []).find(
          (li) =>
            li.type === "material" &&
            (li.poItemCode === paired.material?.poItemCode ||
              li.description === paired.material?.name),
        );
        if (matRow && matRow.quantity > 0) {
          pieceQty = String(matRow.quantity);
        } else if (totalCoverage > 0 && l > 0) {
          const raw =
            isSF(laborUom) && h > 0
              ? totalCoverage / (h * l)
              : totalCoverage / l;
          const derived = Math.round(raw);
          pieceQty = derived > 0 ? String(derived) : "";
        }
        if (isLF(laborUom) && l > 0) {
          const cleanInches = Math.round(l * 12 * 4) / 4;
          lengthIn = String(cleanInches);
        }
        if (isSF(laborUom) && h > 0 && l > 0) {
          combinedDim = `${h}x${l}`;
          heightStr = String(h);
          lengthStr = String(l);
        }
      } else if (isLF(laborUom)) {
        // Labor-only LF:
        // qty = piece count (totalCoverage / lengthFt), not raw coverage
        // lengthIn = the per-piece length stored on the row
        if (l > 0) {
          const cleanInches = Math.round(l * 12 * 4) / 4;
          lengthIn = String(cleanInches);
          const derived = Math.round(totalCoverage / l);
          pieceQty = derived > 0 ? String(derived) : "";
        } else {
          pieceQty =
            totalCoverage > 0
              ? String(totalCoverage)
              : storedQty > 0
                ? String(storedQty)
                : "";
        }
      } else if (isSF(laborUom)) {
        // Labor-only SF:
        // qty = piece count (totalCoverage / (h*l)), not raw coverage
        // dims = per-piece height/length
        if (h > 0 && l > 0) {
          combinedDim = `${h}x${l}`;
          heightStr = String(h);
          lengthStr = String(l);
          const derived = Math.round(totalCoverage / (h * l));
          pieceQty = derived > 0 ? String(derived) : "";
        } else {
          pieceQty =
            totalCoverage > 0
              ? String(totalCoverage)
              : storedQty > 0
                ? String(storedQty)
                : "";
        }
      } else {
        // EA / count-based
        pieceQty = String(storedQty || "");
      }

      instances.push(
        makeInst(pIdx, {
          qty: pieceQty,
          lengthIn,
          height: heightStr,
          length: lengthStr,
          combinedDim,
        }),
      );
    });
  });

  return instances;
}

// ---------------------------------------------------------------------------
// Compact items breakdown — derived straight from request.lineItems, no
// module fetch needed. Mirrors LaborSummaryForeman in the mobile app so
// foremen see pcs / dims / coverage / rate without expanding the card.
// ---------------------------------------------------------------------------
const isHourlyUom = (uom) => (uom || "").toUpperCase() === "HR";

function buildItemGroups(lineItems) {
  const laborRows = (lineItems || []).filter(
    (li) =>
      li.type !== "material" &&
      !li.poItemCode &&
      !!li.itemCode &&
      !isHourlyUom(li.uom),
  );
  const groups = [];
  const seen = new Map();
  laborRows.forEach((li) => {
    const key = li.moduleItemId || "";
    if (!key) {
      groups.push({ key: `flat_${groups.length}`, rows: [li] });
      return;
    }
    if (!seen.has(key)) {
      const g = { key, rows: [] };
      seen.set(key, g);
      groups.push(g);
    }
    seen.get(key).rows.push(li);
  });
  return groups;
}

const derivePieces = (li) => {
  const sf = isSF(li.uom);
  const lf = isLF(li.uom);
  const coverage =
    li.computedCoverage > 0 ? li.computedCoverage : li.quantity || 0;
  const h = li.height || 0;
  const l = li.length || 0;
  if (sf && h > 0 && l > 0) return Math.round(coverage / (h * l)) || 1;
  if (lf && l > 0) return Math.round(coverage / l) || 1;
  return coverage;
};

function ItemsBreakdownPreview({ lineItems }) {
  const groups = useMemo(() => buildItemGroups(lineItems), [lineItems]);
  const hrRow = (lineItems || []).find(
    (li) => isHourlyUom(li.uom) && li.type !== "material" && !!li.itemCode,
  );

  const visibleGroups = groups.filter((g) => {
    const totalQty = g.rows.reduce(
      (s, r) =>
        s + (r.computedCoverage > 0 ? r.computedCoverage : r.quantity || 0),
      0,
    );
    return totalQty > 0;
  });

  if (!visibleGroups.length && !(hrRow && (hrRow.quantity || 0) > 0))
    return null;

  return (
    <div className="px-4 pt-2 pb-3 border-t border-gray-700/40 space-y-1.5">
      {visibleGroups.map((g) => {
        const first = g.rows[0];
        const uom = first.uom || "";
        const sf = isSF(uom);
        const lf = isLF(uom);
        const rate = first.unitRate || 0;
        const totalQty = g.rows.reduce(
          (s, r) =>
            s + (r.computedCoverage > 0 ? r.computedCoverage : r.quantity || 0),
          0,
        );
        const sub = parseFloat((totalQty * rate).toFixed(2));

        return (
          <div
            key={g.key}
            className="rounded-lg border border-gray-700/60 bg-gray-900/40 px-2.5 py-2"
          >
            <div className="flex items-center justify-between gap-2">
              <span className="text-xs text-gray-300 truncate">
                {first.description}
              </span>
              <span className="text-xs font-mono font-semibold text-orange-300 shrink-0">
                ${fmt(sub)}
              </span>
            </div>
            <div className="flex items-center gap-1.5 mt-1 flex-wrap">
              <span className="text-[10px] font-mono text-blue-300">
                {fmtQty(totalQty)}{" "}
                <span className="text-gray-500 font-normal">{uom}</span>
              </span>
              <span className="text-[10px] text-gray-600">·</span>
              <span className="text-[10px] font-mono text-gray-500">
                ${fmtRate(rate)}/{uom}
              </span>
              {(sf || lf) &&
                g.rows.map((li, i) => {
                  const pcs = derivePieces(li);
                  return (
                    <span
                      key={i}
                      className="text-[10px] bg-gray-800 border border-gray-700 text-gray-400 px-1.5 py-0.5 rounded"
                    >
                      {pcs} {pcs === 1 ? "pc" : "pcs"}
                      {sf &&
                        li.height > 0 &&
                        li.length > 0 &&
                        ` · ${fmtQty(li.height)}×${fmtQty(li.length)} ft`}
                      {lf && li.length > 0 && ` · ${fmtQty(li.length)} ft`}
                    </span>
                  );
                })}
            </div>
          </div>
        );
      })}

      {hrRow && (hrRow.quantity || 0) > 0 && (
        <div className="rounded-lg border border-gray-700/60 bg-gray-900/40 px-2.5 py-2">
          <div className="flex items-center justify-between gap-2">
            <span className="text-xs text-gray-300">Labour Hours</span>
            <span className="text-xs font-mono font-semibold text-orange-300">
              ${fmt((hrRow.quantity || 0) * (hrRow.unitRate || 0))}
            </span>
          </div>
          <span className="text-[10px] font-mono text-blue-300">
            {fmtQty(hrRow.quantity)}{" "}
            <span className="text-gray-500 font-normal">HR</span> · $
            {fmtRate(hrRow.unitRate)}/hr
          </span>
        </div>
      )}
    </div>
  );
}

// ─── WorkerPicker (web) ──────────────────────────────────────────────────────
function WorkerPicker({ workers, selected, onSelect, loading }) {
  const [query, setQuery] = useState("");
  const filtered = useMemo(() => {
    if (!query.trim()) return workers;
    const q = query.toLowerCase();
    return workers.filter(
      (w) =>
        (w.displayName || "").toLowerCase().includes(q) ||
        (w.email || "").toLowerCase().includes(q),
    );
  }, [workers, query]);

  if (loading)
    return (
      <div className="flex items-center gap-2 py-4 justify-center">
        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-orange-500" />
        <span className="text-xs text-gray-400">Loading workers…</span>
      </div>
    );

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2 px-2.5 py-1.5 bg-gray-700 border border-gray-600 rounded-lg focus-within:border-orange-500 transition-colors">
        <svg
          className="w-3.5 h-3.5 text-gray-500 shrink-0"
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path
            fillRule="evenodd"
            d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z"
            clipRule="evenodd"
          />
        </svg>
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search worker name or email…"
          className="flex-1 text-xs bg-transparent text-gray-200 placeholder-gray-500 focus:outline-none"
        />
        {query && (
          <button
            onClick={() => setQuery("")}
            className="text-gray-500 hover:text-gray-300 text-sm leading-none"
          >
            ×
          </button>
        )}
      </div>
      <div className="max-h-48 overflow-y-auto space-y-1">
        {filtered.map((w) => {
          const active = selected?._id === w._id;
          return (
            <button
              key={w._id}
              onClick={() => onSelect(active ? null : w)}
              className={cls(
                "w-full flex items-center gap-2.5 px-3 py-2 rounded-lg border text-left transition-colors",
                active
                  ? "bg-orange-900/20 border-orange-700/40 text-orange-300"
                  : "bg-gray-700 border-gray-600 text-gray-200 hover:bg-gray-600",
              )}
            >
              <div
                className={cls(
                  "w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold shrink-0",
                  active
                    ? "bg-orange-500/20 text-orange-400"
                    : "bg-gray-600 text-gray-400",
                )}
              >
                {(w.displayName || w.email || "?")[0].toUpperCase()}
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-xs font-medium truncate">
                  {w.displayName || w.email}
                </p>
                {w.email && w.displayName && (
                  <p className="text-[10px] text-gray-500 truncate">
                    {w.email}
                  </p>
                )}
              </div>
              {active && (
                <svg
                  className="w-4 h-4 text-orange-400 shrink-0"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
              )}
            </button>
          );
        })}
        {filtered.length === 0 && (
          <p className="text-xs text-gray-500 text-center py-3">
            No workers match "{query}"
          </p>
        )}
      </div>
    </div>
  );
}
// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
export default function EditableRequestCard({
  request: initialRequest,
  token,
  onConfirm,
  onReject,
  acting,
  onUpdated,
  initialExpanded,
  isManager, // NEW — passed from PieceworkRequestsTab
  onEditModeChange, // NEW — notifies parent modal so backdrop click can be suppressed while editing
}) {
  const [request, setRequest] = useState(initialRequest);
  const [expanded, setExpanded] = useState(initialExpanded || false);
  const [editMode, setEditMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [workDate, setWorkDate] = useState(
    request.workCompletedDate
      ? request.workCompletedDate.slice(0, 10)
      : new Date().toISOString().slice(0, 10),
  );
  const [module, setModule] = useState(null);
  const [loadingModule, setLoadingModule] = useState(false);
  const [instances, setInstances] = useState([]);
  const [sizeErrors, setSizeErrors] = useState(new Set());
  const [hoursWorked, setHoursWorked] = useState("");
  const [entries, setEntries] = useState({});
  const [manualItems, setManualItems] = useState([]);
  const [totalBurdenPct, setTotalBurdenPct] = useState(0);
  const workerBurdenMultiplier = 1 + totalBurdenPct / 100;
  const [notes, setNotes] = useState(request.notes || "");
  const hstRate = request.hstRate || 13;

  // ── Worker picker (manager/admin only) ──────────────────────────────────────
  const [pieceworkers, setPieceworkers] = useState([]);
  const [loadingWorkers, setLoadingWorkers] = useState(false);
  const [selectedWorker, setSelectedWorker] = useState(null);
  const [showWorkerPicker, setShowWorkerPicker] = useState(false);

  const isSubmitted = request.status === "submitted";
  const isConfirmed = request.status === "confirmed";
  const isRejected = request.status === "rejected";

  const headers = { Authorization: `Bearer ${token}` };

  const hrUnitRate = useMemo(() => {
    const hrRow = (request.lineItems || []).find(
      (li) => li.uom === "HR" && li.type !== "material",
    );
    return hrRow?.unitRate || 0;
  }, [request.lineItems]);

  useEffect(() => {
    if (onEditModeChange) onEditModeChange(editMode);
  }, [editMode, onEditModeChange]);

  // Burden rate
  useEffect(() => {
    if (!request.workOrder?._id || !request.worker?._id) return;
    axios
      .get(`${API}/pieceworkerLogs3/burden-rate`, {
        headers,
        params: {
          workOrderId: request.workOrder._id,
          workerId: request.worker._id,
        },
      })
      .then((r) => setTotalBurdenPct(r.data?.totalBurdenPct || 0))
      .catch(() => setTotalBurdenPct(0));
  }, [request.workOrder?._id, request.worker?._id]);

  // Prevent scroll from changing focused <input type="number"> values.
  // Number inputs interpret wheel events as up/down arrows when focused;
  // since these forms sit inside scrollable containers, an innocent scroll
  // silently mutates quantities/rates. Blur the input before the scroll
  // takes effect so the page scrolls normally instead.
  useEffect(() => {
    const handler = (e) => {
      const el = document.activeElement;
      if (el && el.tagName === "INPUT" && el.type === "number") {
        el.blur();
      }
    };
    document.addEventListener("wheel", handler, { passive: true });
    return () => document.removeEventListener("wheel", handler);
  }, []);

  // Fetch pieceworkers for manager worker-picker
  useEffect(() => {
    if (!isManager || !expanded) return;
    if (pieceworkers.length > 0) return; // already loaded
    setLoadingWorkers(true);
    axios
      .get(`${API}/auth/users`, {
        headers,
        params: { role: "worker", limit: 200 },
      })
      .then((r) => {
        // Support both /auth/users and a /mobile/users/pieceworkers shape
        const raw =
          r.data.users || r.data.data || (Array.isArray(r.data) ? r.data : []);
        setPieceworkers(
          raw.filter((u) => u.role === "worker" || u.role === "pieceworker"),
        );
      })
      .catch(() => {
        // Fallback: try the mobile endpoint
        axios
          .get(`${API}/mobile/users/pieceworkers`, { headers })
          .then((r) => setPieceworkers(Array.isArray(r.data) ? r.data : []))
          .catch(() => setPieceworkers([]));
      })
      .finally(() => setLoadingWorkers(false));
  }, [isManager, expanded]);

  // Pre-select current worker when entering edit mode
  useEffect(() => {
    if (editMode && isManager && request.worker && !selectedWorker) {
      setSelectedWorker(request.worker);
    }
  }, [editMode, isManager]);

  // ---------------------------------------------------------------------------
  // Load module whenever card is expanded OR request.lineItems changes.
  // Depending on request.lineItems ensures a re-save triggers a fresh load.
  // ---------------------------------------------------------------------------
  // ---------------------------------------------------------------------------
  // Load module whenever card is expanded OR request.lineItems changes.
  // Depending on request.lineItems ensures a re-save triggers a fresh load.
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!expanded) return;
    const projectId = request.project?._id || request.project;

    if (request.moduleId) {
      setLoadingModule(true);
      axios
        .get(`${API}/pieceworkRequests/module-admin/list`, {
          headers,
          params: { projectId },
        })
        .then((r) => {
          const found = (r.data.modules || []).find(
            (m) => m._id === (request.moduleId?._id || request.moduleId),
          );
          if (found) {
            setModule(found);

            // Always rebuild instances fresh from module + current lineItems
            const insts = buildInitialInstances(found, request.lineItems);
            setInstances(insts);
            setSizeErrors(new Set());

            // Pre-fill hours from saved HR row
            const hrRow = (request.lineItems || []).find(
              (li) => li.uom === "HR" && li.type !== "material",
            );
            setHoursWorked(hrRow ? String(hrRow.quantity || "") : "");

            // Build legacy entries for read-only display
            const init = {};
            (found.pairedItems || []).forEach((paired, idx) => {
              const laborUom = paired.labor?.uom || "";
              const laborRow = findLaborRowForPaired(
                request.lineItems,
                found._id,
                idx,
                paired,
              );
              const matRow = paired.material
                ? (request.lineItems || []).find(
                    (li) =>
                      li.type === "material" &&
                      li.poItemCode === paired.material?.poItemCode,
                  )
                : null;

              if (laborRow) {
                const h = laborRow.height || 0;
                const l = laborRow.length || 0;
                let pieceQty = "";
                if (paired.material) {
                  if (matRow && matRow.quantity > 0) {
                    pieceQty = String(matRow.quantity);
                  } else {
                    const totalCov = laborRow.quantity || 0;
                    if (totalCov > 0 && l > 0) {
                      const dp =
                        isSF(laborUom) && h > 0
                          ? Math.round(totalCov / (h * l))
                          : Math.round(totalCov / l);
                      pieceQty = dp > 0 ? String(dp) : "";
                    }
                  }
                } else {
                  pieceQty = String(laborRow.quantity || "");
                }
                init[idx] = {
                  qty: pieceQty,
                  height: h > 0 ? String(h) : "",
                  length: l > 0 ? String(l) : "",
                };
              } else {
                init[idx] = { qty: "", height: "", length: "" };
              }
            });
            setEntries(init);
          }
        })
        .catch(() => {})
        .finally(() => setLoadingModule(false));
    } else {
      // No module — manual items (HR excluded)
      setManualItems(
        (request.lineItems || [])
          .filter(
            (li) =>
              li.type !== "material" &&
              !li.poItemCode &&
              !!li.itemCode &&
              li.uom !== "HR",
          )
          .map((li) => ({
            ...li,
            height: li.height || 0,
            length: li.length || 0,
            quantity: li.quantity || 0,
          })),
      );
      const hrRow = (request.lineItems || []).find(
        (li) => li.uom === "HR" && li.type !== "material",
      );
      setHoursWorked(hrRow ? String(hrRow.quantity || "") : "");
    }
  }, [expanded, request._id, request.lineItems]);

  // Instance handlers
  const handleInstChange = useCallback((instId, field, value) => {
    setInstances((prev) =>
      prev.map((inst) =>
        inst._instId === instId ? { ...inst, [field]: value } : inst,
      ),
    );
    setSizeErrors(new Set());
  }, []);

  const handleDuplicate = useCallback((instId) => {
    setInstances((prev) => {
      const idx = prev.findIndex((i) => i._instId === instId);
      if (idx === -1) return prev;
      const orig = prev[idx];
      const copy = makeInst(orig.pairedIdx, {
        height: orig.height,
        length: orig.length,
        combinedDim: orig.combinedDim,
        lengthIn: orig.lengthIn,
        qty: "",
      });
      const next = [...prev];
      next.splice(idx + 1, 0, copy);
      return next;
    });
    setSizeErrors(new Set());
  }, []);

  const handleRemove = useCallback((instId) => {
    setInstances((prev) => prev.filter((i) => i._instId !== instId));
    setSizeErrors(new Set());
  }, []);

  const effectiveHstRate = useMemo(() => {
    const worker = request.worker;
    if (!worker) return 0;
    // Only show HST when collectsTax is explicitly true
    if (worker.collectsTax === true)
      return parseFloat(worker.taxRate || hstRate) || hstRate;
    // false, undefined, or missing — no HST
    return 0;
  }, [request.worker, hstRate]);

  const updateManualItem = useCallback((idx, field, value) => {
    setManualItems((prev) =>
      prev.map((item, i) =>
        i !== idx
          ? item
          : {
              ...item,
              [field]: field === "notes" ? value : parseFloat(value) || 0,
            },
      ),
    );
  }, []);

  const removeManualItem = useCallback((idx) => {
    setManualItems((prev) => prev.filter((_, i) => i !== idx));
  }, []);

  const sizeKeyLocal = (inst, laborUom) => {
    if (isSF(laborUom)) {
      const cd = (inst.combinedDim || "").trim();
      if (cd) return cd.toLowerCase().replace(/\s+/g, "");
      return `${resolveEntryFt(inst.height).toFixed(4)}x${resolveEntryFt(inst.length).toFixed(4)}`;
    }
    if (isLF(laborUom)) {
      const inches = parseInches(String(inst.lengthIn ?? ""));
      return inches !== null
        ? inches.toFixed(4)
        : String(inst.lengthIn ?? "").trim();
    }
    return "";
  };

  // ---------------------------------------------------------------------------
  // Build lineItems for PATCH — identical structure to CreatePieceworkRequest
  // ---------------------------------------------------------------------------
  const buildLineItems = useCallback(() => {
    const lineItems = [];

    if (module) {
      const byPaired = new Map();
      for (const inst of instances) {
        if (!byPaired.has(inst.pairedIdx)) byPaired.set(inst.pairedIdx, []);
        byPaired.get(inst.pairedIdx).push(inst);
      }
      for (const [pIdx, insts] of byPaired.entries()) {
        const paired = module.pairedItems?.[pIdx];
        if (!paired) continue;
        for (const inst of insts) {
          const lQty = calcLaborQtyInst(paired, inst);
          if (lQty <= 0) continue;
          const qty = parseFloat(inst.qty) || 0;
          const heightFt = resolveEntryFt(inst.height);
          const lengthFt = resolveEntryFt(inst.length);
          const lengthInVal = parseInches(String(inst.lengthIn ?? "")) ?? 0;
          const lfLengthFt = lengthInVal / 12;

          if (paired.material && qty > 0) {
            lineItems.push({
              type: "material",
              description: paired.material.name || "",
              uom: paired.material.uom || "",
              quantity: qty,
              height: heightFt,
              length: paired.material.uom?.toUpperCase().includes("LF")
                ? lfLengthFt
                : lengthFt,
              sectionNumber: "",
              poItemCode: "",
            });
          }

          lineItems.push({
            budgetItemId: null,
            itemCode: paired.labor.itemCode,
            description: paired.labor.description,
            sectionNumber: "",
            sectionName: "",
            bidArea: request.workOrder?.bidArea || "",
            areaType: request.areaType || "",
            uom: paired.labor.uom,
            height: heightFt,
            length: paired.labor.uom?.toUpperCase().includes("LF")
              ? lfLengthFt
              : lengthFt,
            computedCoverage: lQty,
            quantity: lQty,
            unitRate: paired.labor.unitRate || 0,
            rateSource: paired.labor.rateSource || "db",
            moduleItemId: `${module._id}_${pIdx}`,
            notes: "",
          });
        }
      }
    } else {
      manualItems.forEach((item) => {
        const qty = calcManualQty(item);
        if (qty <= 0) return;
        lineItems.push({
          budgetItemId: item.budgetItemId || item._id,
          itemCode: item.itemCode,
          description: item.description,
          uom: item.uom,
          height: item.height || 0,
          length: item.length || 0,
          computedCoverage: requiresDims(item.uom)
            ? isSF(item.uom)
              ? (item.height || 0) * (item.length || 0)
              : item.length || 0
            : 0,
          quantity: qty,
          unitRate: item.unitRate,
          rateSource: item.rateSource || "db",
          areaType: item.areaType || request.areaType || "",
          sectionNumber: item.sectionNumber || "",
          sectionName: item.sectionName || "",
          bidArea: item.bidArea || "",
        });
      });
    }

    // HR row — rate from saved line item, not from /auth/me
    const hrs = parseFloat(hoursWorked) || 0;
    const savedHrRow = (request.lineItems || []).find(
      (li) => li.uom === "HR" && li.type !== "material",
    );
    if (hrs > 0 && (hrUnitRate > 0 || savedHrRow)) {
      lineItems.push({
        budgetItemId: null,
        itemCode: savedHrRow?.itemCode || "LABOUR-HR",
        description: savedHrRow?.description || "Labour Hours",
        sectionNumber: savedHrRow?.sectionNumber || "",
        sectionName: savedHrRow?.sectionName || "",
        bidArea: request.workOrder?.bidArea || "",
        areaType: request.areaType || "",
        uom: "HR",
        height: 0,
        length: 0,
        computedCoverage: 0,
        quantity: hrs,
        unitRate: hrUnitRate || savedHrRow?.unitRate || 0,
        rateSource: "manual",
        moduleItemId: "",
        notes: "Labour hours",
      });
    }

    return lineItems;
  }, [module, instances, manualItems, hoursWorked, hrUnitRate, request]);

  // Collapsed header totals
  const savedLaborItems = useMemo(
    () =>
      (request.lineItems || []).filter(
        (li) =>
          li.type !== "material" &&
          !li.poItemCode &&
          !!li.itemCode &&
          li.uom !== "HR",
      ),
    [request.lineItems],
  );

  const savedLaborSubtotal = useMemo(() => {
    let s = 0;
    savedLaborItems.forEach((li) => {
      const needsDims = requiresDims(li.uom);
      const qty = needsDims
        ? li.computedCoverage || li.quantity || 0
        : li.quantity || 0;
      s += qty * (li.unitRate || 0);
    });
    return parseFloat(s.toFixed(2));
  }, [savedLaborItems]);

  const savedHrSubtotal = useMemo(() => {
    const hrRow = (request.lineItems || []).find(
      (li) => li.uom === "HR" && li.type !== "material",
    );
    if (!hrRow || (hrRow.quantity || 0) <= 0) return 0;
    return parseFloat(
      ((hrRow.quantity || 0) * (hrRow.unitRate || 0)).toFixed(2),
    );
  }, [request.lineItems]);

  const savedLaborHst = parseFloat(
    (((savedLaborSubtotal + savedHrSubtotal) * effectiveHstRate) / 100).toFixed(
      2,
    ),
  );
  const savedLaborTotal = savedLaborSubtotal + savedHrSubtotal + savedLaborHst;

  // savedCoveragesMap — only non-zero entries so zero-qty rows stay hidden
  // savedCoveragesMap — only non-zero entries so zero-qty rows stay hidden
  // savedCoveragesMap — only non-zero entries so zero-qty rows stay hidden
  // savedCoveragesMap — only non-zero entries so zero-qty rows stay hidden
  const { savedCoveragesMap, savedPieceCountsMap } = useMemo(() => {
    if (!module) return { savedCoveragesMap: {}, savedPieceCountsMap: {} };
    const coverages = {};
    const pieces = {};

    (module.pairedItems || []).forEach((paired, idx) => {
      const laborRow = findLaborRowForPaired(
        request.lineItems,
        module._id,
        idx,
        paired,
      );
      if (!laborRow) return; // absent key = row hidden in read-only

      const laborUom = laborRow.uom || paired.labor?.uom || "";
      const h = laborRow.height || 0;
      const l = laborRow.length || 0;

      const matRow = paired.material
        ? (request.lineItems || []).find(
            (li) =>
              li.type === "material" &&
              li.poItemCode === paired.material?.poItemCode,
          )
        : null;
      const pieceCount = matRow?.quantity ?? null;
      if (paired.material && pieceCount != null) pieces[idx] = pieceCount;

      let cov = 0;
      if (!requiresDims(laborUom)) {
        cov = laborRow.quantity || 0;
      } else if (paired.material) {
        cov =
          laborRow.quantity > 0
            ? laborRow.quantity
            : isSF(laborUom)
              ? parseFloat(((pieceCount || 1) * h * l).toFixed(2))
              : parseFloat(((pieceCount || 1) * l).toFixed(2));
      } else {
        cov =
          laborRow.computedCoverage > 0
            ? laborRow.computedCoverage
            : laborRow.quantity > 0
              ? laborRow.quantity
              : isSF(laborUom)
                ? parseFloat((h * l).toFixed(2))
                : l;
      }

      if (cov > 0) coverages[idx] = cov;
    });

    return { savedCoveragesMap: coverages, savedPieceCountsMap: pieces };
  }, [module, request.lineItems]);

  // HR row surfaced as a manualItem for read-only display
  const readOnlyManualItems = useMemo(() => {
    const hrRow = (request.lineItems || []).find(
      (li) => li.uom === "HR" && li.type !== "material" && !!li.itemCode,
    );
    if (!hrRow || (hrRow.quantity || 0) <= 0) return [];
    return [
      { ...hrRow, computedCoverage: hrRow.quantity || 0, height: 0, length: 0 },
    ];
  }, [request.lineItems]);

  // Enter edit mode — always rebuild instances from current module + lineItems
  const enterEditMode = () => {
    if (module) {
      const insts = buildInitialInstances(module, request.lineItems);
      setInstances(insts);
    }
    setSizeErrors(new Set());
    setSaveError("");
    setEditMode(true);
  };

  // ---------------------------------------------------------------------------
  // Save
  // ---------------------------------------------------------------------------
  // ── Replace handleSave ────────────────────────────────────────────────────
  const handleSave = async () => {
    if (module) {
      const errs = validateEntries(instances, module);
      if (errs.length > 0) {
        const errSet = new Set();
        const byPaired = new Map();
        for (const inst of instances) {
          if (!byPaired.has(inst.pairedIdx)) byPaired.set(inst.pairedIdx, []);
          byPaired.get(inst.pairedIdx).push(inst);
        }
        for (const [pIdx, insts] of byPaired.entries()) {
          if (insts.length < 2) continue;
          const paired = module.pairedItems?.[pIdx];
          if (!paired) continue;
          const uom = paired.labor?.uom || "";
          const seen = new Map();
          for (const inst of insts) {
            const k = sizeKeyLocal(inst, uom);
            if (k && seen.has(k)) {
              errSet.add(inst._instId);
              errSet.add(seen.get(k));
            } else if (k) {
              seen.set(k, inst._instId);
            }
          }
        }
        setSizeErrors(errSet);
        setSaveError(errs.join(" "));
        return;
      }
    }

    if (isManager && showWorkerPicker && !selectedWorker) {
      setSaveError("Please select a worker before saving.");
      return;
    }

    setSaving(true);
    setSaveError("");
    try {
      const lineItems = buildLineItems();
      const submittableItems = lineItems.filter(
        (li) => li.type !== "material" && !li.poItemCode && !!li.itemCode,
      );
      if (!submittableItems.length) {
        setSaveError("Please enter quantities for at least one labor item.");
        setSaving(false);
        return;
      }

      const payload = {
        lineItems,
        notes,
        workCompletedDate: workDate,
      };
      // If manager changed the worker, include workerId in the patch
      if (
        isManager &&
        selectedWorker &&
        selectedWorker._id !== request.worker?._id
      ) {
        payload.workerId = selectedWorker._id;
      }

      const res = await axios.patch(
        `${API}/pieceworkRequests/${request._id}`,
        payload,
        { headers },
      );
      const updated = res.data.data;
      setRequest(updated);
      setNotes(updated.notes || "");
      setWorkDate(
        updated.workCompletedDate
          ? updated.workCompletedDate.slice(0, 10)
          : new Date().toISOString().slice(0, 10),
      );
      setEditMode(false);
      setExpanded(false);
      setModule(null);
      setInstances([]);
      setSizeErrors(new Set());
      setEntries({});
      setManualItems([]);
      setShowWorkerPicker(false);
      if (onUpdated) onUpdated(updated);
    } catch (e) {
      setSaveError(e.response?.data?.message || e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleConfirm = async () => {
    if (editMode) await handleSave();
    onConfirm(request._id);
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  return (
    <div
      className={cls(
        "bg-gray-800 rounded-xl border overflow-hidden transition-colors",
        STATUS_BORDER[request.status] || "border-gray-700",
      )}
    >
      {/* Collapsed header */}
      <div
        className="px-4 py-4 flex items-center gap-3 cursor-pointer hover:bg-gray-700/30 transition-colors"
        onClick={() => {
          setExpanded((p) => !p);
          if (!expanded) setEditMode(false);
        }}
      >
        <div
          className={cls(
            "w-2 h-2 rounded-full shrink-0",
            isSubmitted
              ? "bg-yellow-400 animate-pulse"
              : isConfirmed
                ? "bg-green-400"
                : isRejected
                  ? "bg-red-400"
                  : "bg-gray-600",
          )}
        />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-0.5">
            <span className="text-xs font-mono text-orange-400">
              {request.requestNumber}
            </span>
            <span
              className={cls(
                "px-2 py-0.5 text-[10px] font-semibold rounded-full",
                STATUS_BADGE[request.status] || "bg-gray-700/60 text-gray-300",
              )}
            >
              {request.status?.toUpperCase()}
            </span>
            {request.moduleName && (
              <span className="text-[10px] text-gray-500 bg-gray-700/60 border border-gray-600 px-1.5 py-0.5 rounded-full">
                {request.moduleName}
              </span>
            )}
          </div>
          <p className="text-sm font-semibold text-white truncate">
            {request.worker?.displayName || request.worker?.email || "Worker"}
          </p>
          <div className="flex flex-wrap gap-x-3 mt-0.5">
            <span className="text-[11px] text-gray-400">
              {request.workOrder?.woNumber} — {request.workOrder?.title}
            </span>
            {request.workCompletedDate && (
              <span className="text-[11px] text-gray-500">
                {format(parseISO(request.workCompletedDate), "MMM d, yyyy")}
              </span>
            )}
            {request.areaType && (
              <span className="text-[11px] text-gray-500">
                {request.areaType}
              </span>
            )}
          </div>
        </div>
        <div className="text-right shrink-0">
          <p className="text-[10px] text-gray-500">
            {savedLaborItems.length} labor item
            {savedLaborItems.length !== 1 ? "s" : ""}
          </p>
          <p className="text-sm font-bold text-orange-300 font-mono">
            ${fmt(savedLaborTotal)}
          </p>
          {effectiveHstRate > 0 && (
            <p className="text-[10px] text-gray-600">labor + HST</p>
          )}
        </div>
        <svg
          className={cls(
            "w-4 h-4 text-gray-500 shrink-0 transition-transform",
            expanded && "rotate-180",
          )}
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path
            fillRule="evenodd"
            d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
            clipRule="evenodd"
          />
        </svg>
      </div>

      {request.notes && (
        <div className="px-4 pt-2 pb-1 border-t border-gray-700/40">
          <p className="text-[11px] text-gray-500 italic truncate">
            "{request.notes}"
          </p>
        </div>
      )}

      <ItemsBreakdownPreview lineItems={request.lineItems} />

      {/* Expanded body */}
      {expanded && (
        <div className="border-t border-gray-700/60 space-y-4 px-4 py-4">
          {/* Context summary */}
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
            {[
              ["Worker", request.worker?.displayName],
              [
                "Work Completed",
                request.workCompletedDate
                  ? format(parseISO(request.workCompletedDate), "MMM d, yyyy")
                  : "—",
              ],
              [
                "Pay Period",
                request.workCompletedDate
                  ? `Week of ${format(parseISO(request.workCompletedDate), "MMM d, yyyy")}`
                  : "—",
              ],
              ["Area", request.areaType || "—"],
              [
                "Submitted",
                request.submittedAt
                  ? format(parseISO(request.submittedAt), "MMM d, h:mm a")
                  : "—",
              ],
            ].map(([label, val]) => (
              <div
                key={label}
                className="bg-gray-900 rounded-lg border border-gray-700 p-2.5"
              >
                <p className="text-[10px] text-gray-500 uppercase tracking-wide mb-0.5">
                  {label}
                </p>
                <p className="text-xs text-gray-200 font-medium truncate">
                  {val || "—"}
                </p>
              </div>
            ))}
          </div>

          {/* ── SUBMITTED ── */}
          {isSubmitted && (
            <>
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-white">
                  {editMode ? "Editing Items" : "Labor Items"}
                </h3>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    if (editMode) {
                      setEditMode(false);
                      setSaveError("");
                      setSizeErrors(new Set());
                    } else {
                      enterEditMode();
                    }
                  }}
                  className={cls(
                    "px-3 py-1 text-xs font-medium rounded-lg border transition-colors",
                    editMode
                      ? "text-gray-300 bg-gray-700 border-gray-600 hover:bg-gray-600"
                      : "text-orange-300 bg-orange-900/20 border-orange-700/40 hover:border-orange-600/60",
                  )}
                >
                  {editMode ? "Cancel Edit" : "✏ Edit Items"}
                </button>
              </div>

              {saveError && (
                <div className="px-3 py-2 bg-red-900/40 border border-red-700/50 rounded-lg text-xs text-red-300">
                  {saveError}
                </div>
              )}

              {loadingModule ? (
                <div className="flex items-center gap-2 py-6 justify-center">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-orange-500" />
                  <span className="text-sm text-gray-400">Loading…</span>
                </div>
              ) : editMode ? (
                <>
                  {/* Work Completed Date */}
                  <div className="flex items-center gap-3 px-3 py-2.5 bg-gray-800 border border-gray-700 rounded-xl">
                    <div className="flex-1">
                      <label className="text-[10px] text-gray-500 uppercase tracking-wide block mb-1">
                        Work Completed Date
                      </label>
                      <input
                        type="date"
                        value={workDate}
                        max={new Date().toISOString().slice(0, 10)}
                        onChange={(e) => setWorkDate(e.target.value)}
                        className="w-full px-2 py-1.5 text-sm bg-gray-700 border border-gray-600 rounded-lg text-gray-200 focus:border-orange-500 focus:outline-none"
                      />
                    </div>
                    {workDate !== new Date().toISOString().slice(0, 10) && (
                      <button
                        onClick={() =>
                          setWorkDate(new Date().toISOString().slice(0, 10))
                        }
                        className="text-[10px] text-gray-500 hover:text-gray-300 transition-colors shrink-0 mt-4"
                      >
                        Reset to today
                      </button>
                    )}
                  </div>

                  {/* Worker override (manager only) */}
                  {isManager && (
                    <div className="bg-gray-800 rounded-xl border border-gray-700 p-4 space-y-3">
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="text-sm font-semibold text-white">
                            Worker
                          </h3>
                          <p className="text-[11px] text-gray-500 mt-0.5">
                            Currently:{" "}
                            <span className="text-orange-300 font-medium">
                              {selectedWorker?.displayName ||
                                selectedWorker?.email ||
                                request.worker?.displayName ||
                                request.worker?.email ||
                                "—"}
                            </span>
                          </p>
                        </div>
                        <button
                          onClick={() => setShowWorkerPicker((p) => !p)}
                          className={cls(
                            "px-3 py-1 text-xs font-medium rounded-lg border transition-colors",
                            showWorkerPicker
                              ? "text-gray-300 bg-gray-700 border-gray-600"
                              : "text-orange-300 bg-orange-900/20 border-orange-700/40 hover:border-orange-600",
                          )}
                        >
                          {showWorkerPicker ? "Cancel" : "Change Worker"}
                        </button>
                      </div>

                      {showWorkerPicker && (
                        <WorkerPicker
                          workers={pieceworkers}
                          selected={selectedWorker}
                          onSelect={(w) => {
                            setSelectedWorker(w);
                            if (w) setShowWorkerPicker(false);
                          }}
                          loading={loadingWorkers}
                        />
                      )}

                      {selectedWorker &&
                        selectedWorker._id !== request.worker?._id && (
                          <div className="flex items-center gap-2 px-3 py-2 bg-amber-900/20 border border-amber-700/40 rounded-lg">
                            <svg
                              className="w-3.5 h-3.5 text-amber-400 shrink-0"
                              viewBox="0 0 20 20"
                              fill="currentColor"
                            >
                              <path
                                fillRule="evenodd"
                                d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                                clipRule="evenodd"
                              />
                            </svg>
                            <p className="text-[11px] text-amber-300">
                              This log will be reassigned to{" "}
                              <span className="font-semibold">
                                {selectedWorker.displayName ||
                                  selectedWorker.email}
                              </span>
                            </p>
                          </div>
                        )}
                    </div>
                  )}

                  {/* All module items — fully interactive */}
                  <SharedPieceworkItemEntry
                    module={module}
                    instances={instances}
                    onInstChange={handleInstChange}
                    onDuplicate={handleDuplicate}
                    onRemove={handleRemove}
                    sizeErrors={sizeErrors}
                    manualItems={module ? [] : manualItems}
                    onManualChange={module ? undefined : updateManualItem}
                    onManualRemove={module ? undefined : removeManualItem}
                    hstRate={effectiveHstRate}
                    workerBurdenMultiplier={workerBurdenMultiplier}
                    totalBurdenPct={totalBurdenPct}
                    readOnly={false}
                  />

                  {/* Labour Hours */}
                  <div className="bg-gray-800 rounded-xl border border-gray-700 p-4 space-y-3">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <h3 className="text-sm font-semibold text-white">
                          Labour Hours
                        </h3>
                        <p className="text-[11px] text-gray-500 mt-0.5">
                          Hours worked on this section.
                        </p>
                      </div>
                      {hrUnitRate > 0 && (
                        <div className="text-right shrink-0">
                          <p className="text-[10px] text-gray-500">
                            Hourly rate:
                          </p>
                          <p className="text-sm font-mono font-bold text-green-400">
                            ${fmtRate(hrUnitRate)}/HR
                          </p>
                        </div>
                      )}
                    </div>
                    <div className="grid grid-cols-2 gap-3 items-end">
                      <div>
                        <label className="text-[9px] text-gray-500 uppercase tracking-wide block mb-1">
                          Hours Worked
                        </label>
                        <input
                          type="number"
                          min="0"
                          step="0.25"
                          value={hoursWorked}
                          onChange={(e) => setHoursWorked(e.target.value)}
                          placeholder="e.g. 8 or 7.5"
                          className="w-full px-2 py-1.5 text-sm bg-gray-700 border border-gray-600 rounded-lg font-mono text-gray-200 placeholder-gray-600 focus:border-orange-500 focus:outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                        />
                      </div>
                      {parseFloat(hoursWorked) > 0 && hrUnitRate > 0 && (
                        <div className="px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg">
                          <p className="text-[9px] text-gray-500 uppercase tracking-wide mb-0.5">
                            Est. hourly earnings
                          </p>
                          <p className="text-sm font-mono font-bold text-green-400">
                            ${fmt(parseFloat(hoursWorked) * hrUnitRate)}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </>
              ) : (
                /* READ-ONLY — zero rows hidden, HR row shown via readOnlyManualItems */
                <SharedPieceworkItemEntry
                  module={module}
                  instances={instances}
                  manualItems={readOnlyManualItems}
                  hstRate={effectiveHstRate}
                  workerBurdenMultiplier={1}
                  totalBurdenPct={0}
                  readOnly={true}
                  hideZeroRows={true}
                  overrideLaborSubtotal={savedLaborSubtotal}
                />
              )}

              {/* Notes */}
              {editMode ? (
                <div>
                  <label className="text-xs text-gray-400 uppercase tracking-wide mb-1.5 block">
                    Notes <span className="text-red-400">*</span>
                  </label>
                  <textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    rows={2}
                    placeholder="Unit number and description of work…"
                    className="w-full px-3 py-2 text-sm bg-gray-700 border border-gray-600 rounded-lg text-gray-200 placeholder-gray-500 focus:border-orange-500 focus:outline-none resize-none"
                  />
                </div>
              ) : (
                request.notes && (
                  <div className="bg-gray-900/60 rounded-lg border border-gray-700 px-3 py-2.5">
                    <p className="text-[10px] text-gray-500 uppercase tracking-wide mb-0.5">
                      Notes
                    </p>
                    <p className="text-xs text-gray-300">{request.notes}</p>
                  </div>
                )
              )}

              {editMode && (
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="px-4 py-2 text-sm font-semibold text-white bg-gray-600 rounded-lg hover:bg-gray-500 disabled:opacity-40 transition-colors border border-gray-500"
                >
                  {saving ? "Saving…" : "💾 Save Changes"}
                </button>
              )}

              <div className="flex gap-3 pt-1">
                <button
                  onClick={handleConfirm}
                  disabled={acting || saving}
                  className="flex-1 py-2.5 text-sm font-semibold text-white bg-green-600 rounded-lg hover:bg-green-700 disabled:opacity-40 transition-colors"
                >
                  ✓ Confirm & Create Log
                </button>
                <button
                  onClick={() => onReject(request._id)}
                  disabled={acting || saving}
                  className="px-5 py-2.5 text-sm font-semibold text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-40 transition-colors"
                >
                  ✕ Reject
                </button>
              </div>
            </>
          )}

          {/* ── CONFIRMED ── */}
          {isConfirmed && (
            <>
              <h3 className="text-sm font-semibold text-white">
                Labor Items (Promoted to Log)
              </h3>
              <LaborSummaryTable
                items={request.lineItems}
                hstRate={effectiveHstRate}
                workerBurdenMultiplier={workerBurdenMultiplier}
              />
              {request.promotedLogId && (
                <div className="bg-green-950/30 border border-green-700/40 rounded-lg px-3 py-2.5 flex items-center justify-between">
                  <div>
                    <p className="text-[10px] text-green-400 font-semibold">
                      Piecework Log Created
                    </p>
                    <p className="text-xs text-green-300 font-mono mt-0.5">
                      {request.promotedLogId.logNumber}
                    </p>
                  </div>
                  <span
                    className={cls(
                      "text-[10px] px-2 py-0.5 rounded-full font-semibold",
                      request.promotedLogId.status === "approved"
                        ? "bg-green-700/60 text-green-200"
                        : request.promotedLogId.status === "rejected"
                          ? "bg-red-800/60 text-red-200"
                          : "bg-yellow-700/60 text-yellow-100",
                    )}
                  >
                    {(request.promotedLogId.status || "pending").toUpperCase()}
                  </span>
                </div>
              )}
              {request.notes && (
                <div className="bg-gray-900/60 rounded-lg border border-gray-700 px-3 py-2.5">
                  <p className="text-[10px] text-gray-500 uppercase tracking-wide mb-0.5">
                    Notes
                  </p>
                  <p className="text-xs text-gray-300">{request.notes}</p>
                </div>
              )}
            </>
          )}

          {/* ── REJECTED ── */}
          {isRejected && (
            <>
              <h3 className="text-sm font-semibold text-white">
                Rejected Items
              </h3>
              <LaborSummaryTable
                items={request.lineItems}
                hstRate={effectiveHstRate}
                workerBurdenMultiplier={workerBurdenMultiplier}
              />
              {request.rejectionReason && (
                <div className="bg-red-950/30 border border-red-700/40 rounded-lg px-3 py-2.5">
                  <p className="text-[10px] text-red-400 font-semibold mb-0.5">
                    Rejection Reason
                  </p>
                  <p className="text-xs text-red-300">
                    {request.rejectionReason}
                  </p>
                </div>
              )}
              {request.notes && (
                <div className="bg-gray-900/60 rounded-lg border border-gray-700 px-3 py-2.5">
                  <p className="text-[10px] text-gray-500 uppercase tracking-wide mb-0.5">
                    Notes
                  </p>
                  <p className="text-xs text-gray-300">{request.notes}</p>
                </div>
              )}
            </>
          )}
          <RequestTimeline timeline={request.timeline} />
        </div>
      )}
    </div>
  );
}
